import type { TimeOfDay } from 'generated/openapi';
import { Zap } from 'lucide-react';
import { type HTMLAttributes, memo } from 'react';

import { cn } from '@/shared/utils/cn';

const DAYS_ORDER = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
] as const;

const DAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const TIME_PERIODS: { label: string; value: TimeOfDay }[] = [
  { label: 'Morning', value: 'MORNING' },
  { label: 'Evenings', value: 'EVENING' },
  { label: 'Late night', value: 'NIGHT' },
];

const getSizeClasses = (size: 'default' | 'lg') => {
  return size === 'lg' ? 'size-9' : 'size-4';
};

export interface PlayTimeGridProps extends HTMLAttributes<HTMLDivElement> {
  data: Record<string, TimeOfDay[]> | undefined;
  size?: 'default' | 'lg';
  selectable?: boolean;
  onCellClick?: (day: string, timeOfDay: TimeOfDay) => void;
}

export const PlayTimeGrid = memo(function PlayTimeGrid({
  data,
  size = 'default',
  selectable = false,
  onCellClick,
  ...props
}: PlayTimeGridProps) {
  const iconSize = getSizeClasses(size);

  return (
    <div className="flex flex-col gap-3" {...props}>
      <div className="flex items-start gap-10">
        <div className="w-5 flex-shrink-0" />

        <div className="flex flex-1 items-center justify-between">
          {TIME_PERIODS.map(period => (
            <div key={period.value} className="flex-1 text-center">
              <p className="text-cold-500 text-xs leading-none font-normal">
                {period.label}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {DAYS_ORDER.map((day, rowIndex) => {
          const timeOfDays = data?.[day] ?? [];

          return (
            <div key={day} className="flex items-center gap-10">
              <div className="w-5 flex-shrink-0">
                <p className="text-cold-500 text-center text-xs leading-none font-normal">
                  {DAY_LABELS[rowIndex]}
                </p>
              </div>

              <div className="flex flex-1 items-center justify-between">
                {TIME_PERIODS.map(period => {
                  const isActive = timeOfDays.includes(period.value);

                  return (
                    <div
                      key={`${day}-${period.value}`}
                      className={cn(
                        'flex flex-1 items-center justify-center transition-all',
                        selectable && 'cursor-pointer hover:opacity-80',
                      )}
                      onClick={() => {
                        if (selectable && onCellClick) {
                          onCellClick(day, period.value);
                        }
                      }}
                      onKeyDown={event => {
                        if (
                          selectable &&
                          onCellClick &&
                          (event.key === 'Enter' ||
                            event.key === ' ' ||
                            event.key === 'Spacebar' ||
                            event.key === 'Space')
                        ) {
                          event.preventDefault();
                          onCellClick(day, period.value);
                        }
                      }}
                      role={selectable ? 'button' : undefined}
                      tabIndex={selectable ? 0 : undefined}
                    >
                      <Zap
                        className={cn(
                          iconSize,
                          'transition-colors',
                          isActive
                            ? 'text-purple-brand-400 fill-purple-brand-400'
                            : 'text-cold-600 fill-cold-600 opacity-40',
                        )}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

PlayTimeGrid.displayName = 'PlayTimeGrid';
